import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Crown, Sparkles, Zap, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useQuotaUsage } from '../hooks/useQuotaUsage';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACCENTS = {
  free: { Icon: Sparkles, color: '#94a3b8' },
  plus: { Icon: Zap,      color: '#81d8d0' },
  pro:  { Icon: Crown,    color: '#f59e0b' },
};

export const UpgradeModal = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const { upgradeOpen, closeUpgrade, planId, mostSevere, refresh } = useQuotaUsage();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [yearly, setYearly] = useState(false);

  useEffect(() => {
    if (!upgradeOpen || plans.length) return;
    setLoading(true);
    axios.get(`${API_URL}/api/billing/plans`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => toast.error('Impossible de charger les plans'))
      .finally(() => setLoading(false));
  }, [upgradeOpen, plans.length]);

  const handleCheckout = async (targetPlan) => {
    setSubmitting(targetPlan);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/billing/checkout`,
        { plan_id: targetPlan, period: yearly ? 'yearly' : 'monthly' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        await axios.post(
          `${API_URL}/api/billing/confirm`,
          { plan_id: targetPlan },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Passage au plan ${targetPlan} effectué`);
        await refresh();
        closeUpgrade();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du checkout');
    } finally { setSubmitting(null); }
  };

  return (
    <Dialog open={upgradeOpen} onOpenChange={(o) => !o && closeUpgrade()}>
      <DialogContent
        className={`max-w-3xl ${isDark ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-gray-200'}`}
        data-testid="upgrade-modal"
      >
        <DialogTitle className="text-xl font-semibold tracking-tight">
          {mostSevere ? `Tu approches la limite ${mostSevere.meta.label}` : 'Choisis ton plan'}
        </DialogTitle>
        <DialogDescription className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          Plan actuel : <span className="font-semibold capitalize">{planId}</span>. Passe à un plan supérieur pour ne plus y penser.
        </DialogDescription>

        <div className="flex justify-center mt-2">
          <button
            onClick={() => setYearly(!yearly)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
            data-testid="upgrade-modal-yearly-toggle"
          >
            {yearly ? 'Voir tarif mensuel' : 'Voir tarif annuel (–17%)'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {plans.map((p) => {
              const accent = ACCENTS[p.id] || ACCENTS.free;
              const PlanIcon = accent.Icon;
              const price = yearly ? p.price_yearly : p.price_monthly;
              const period = yearly ? '/an' : '/mois';
              const isCurrent = p.id === planId;
              const isFree = p.id === 'free';
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`relative rounded-xl border p-5 flex flex-col ${
                    p.is_popular
                      ? isDark ? 'border-[#81d8d0]/40 bg-[#81d8d0]/5' : 'border-[#81d8d0] bg-[#81d8d0]/5'
                      : isDark ? 'border-white/10' : 'border-gray-200'
                  }`}
                  data-testid={`upgrade-plan-${p.id}`}
                >
                  {p.is_popular && (
                    <span className="absolute -top-2 left-4 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#81d8d0] text-black">
                      RECOMMANDÉ
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <PlanIcon size={18} style={{ color: accent.color }} />
                    <h3 className="font-semibold">{p.name}</h3>
                  </div>
                  <p className={`text-xs mb-3 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{p.tagline}</p>
                  <div className="mb-4">
                    <span className="text-2xl font-bold">{price === 0 ? 'Gratuit' : `${price} €`}</span>
                    {price > 0 && <span className={`text-xs ml-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{period}</span>}
                  </div>
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: accent.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    disabled={isCurrent || isFree || submitting === p.id}
                    onClick={() => handleCheckout(p.id)}
                    className={`w-full py-2 rounded-lg text-sm font-semibold transition ${
                      isCurrent
                        ? isDark ? 'bg-white/5 text-white/40 cursor-default' : 'bg-gray-100 text-gray-400 cursor-default'
                        : p.is_popular
                          ? 'bg-[#81d8d0] text-black hover:opacity-90'
                          : isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-gray-900 text-white hover:bg-black'
                    }`}
                    data-testid={`upgrade-cta-${p.id}`}
                  >
                    {submitting === p.id ? <Loader2 className="animate-spin mx-auto" size={16} />
                      : isCurrent ? 'Ton plan actuel'
                      : isFree ? 'Plan gratuit'
                      : `Passer à ${p.name}`}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
